import { SEO } from '../components/common/SEO';
import { Layout } from '../components/common/Layout';
import { Button } from '../components/ui/Button';
import { Link } from 'react-router-dom';
import { Play, Sparkles, Users, ArrowRight } from 'lucide-react';
import { useState } from 'react';

export function DemoPage() {
    const [showIosNotice, setShowIosNotice] = useState(false);

    const showIosComingSoon = () => {
        setShowIosNotice(true);
        window.setTimeout(() => setShowIosNotice(false), 3200);
    };

    return (
        <Layout>
            {showIosNotice && (
                <div
                    role="status"
                    aria-live="polite"
                    className="fixed bottom-6 left-1/2 z-[60] w-[calc(100%-2rem)] max-w-sm -translate-x-1/2 rounded-xl bg-gray-950 px-5 py-4 text-center text-sm font-semibold text-white shadow-2xl"
                >
                    iOS app is coming soon. Android is available now.
                </div>
            )}
            <SEO
                title="Swag Shots Demo - Try Before You Download"
                description="Experience Swag Shots's video creation features in our interactive demo. Try filters, effects, and social features before downloading the app."
                url="https://www.swagshots.com/demo"
            />

            {/* Hero Section */}
            <section className="section-padding bg-gradient-to-br from-primary-50 to-primary-100">
                <div className="container-custom text-center">
                    <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
                        Try Swag Shots Demo
                    </h1>
                    <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-8">
                        Experience our video creation platform before downloading. Test features, explore tools, and see what Swag Shots offers.
                    </p>

                    <Button size="lg" className="inline-flex items-center gap-2">
                        <Play className="w-5 h-5" />
                        Launch Interactive Demo
                    </Button>
                </div>
            </section>

            {/* Demo Preview Section */}
            <section className="section-padding">
                <div className="container-custom">
                    <div className="grid lg:grid-cols-2 gap-12 items-center">
                        {/* Demo Interface */}
                        <div className="order-2 lg:order-1">
                            <div className="bg-gray-900 rounded-3xl p-8 max-w-sm mx-auto">
                                <div className="bg-gradient-to-br from-primary-500 to-primary-600 rounded-2xl p-6 text-center text-white">
                                    <div className="w-16 h-16 bg-white/20 rounded-full mx-auto mb-4 flex items-center justify-center">
                                        <Play className="w-8 h-8" />
                                    </div>
                                    <h3 className="text-lg font-semibold mb-2">Demo Ready</h3>
                                    <p className="text-primary-100 text-sm mb-6">
                                        Interactive preview available
                                    </p>
                                    <Button variant="outline" className="w-full bg-white/10 border-white/20 text-white hover:bg-white/20">
                                        Start Demo
                                    </Button>
                                </div>
                            </div>
                        </div>

                        {/* Demo Features */}
                        <div className="order-1 lg:order-2">
                            <h2 className="text-3xl font-bold text-gray-900 mb-6">
                                What You'll Experience
                            </h2>

                            <div className="space-y-6">
                                <div className="flex items-start gap-4">
                                    <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                        <Play className="w-5 h-5 text-primary-600" />
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-gray-900 mb-1">Video Creation Tools</h3>
                                        <p className="text-gray-600">
                                            Test our intuitive recording interface and editing features
                                        </p>
                                    </div>
                                </div>

                                <div className="flex items-start gap-4">
                                    <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                        <Sparkles className="w-5 h-5 text-primary-600" />
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-gray-900 mb-1">Filters & Effects</h3>
                                        <p className="text-gray-600">
                                            Explore our collection of filters, beauty effects, and AR features
                                        </p>
                                    </div>
                                </div>

                                <div className="flex items-start gap-4">
                                    <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                        <Users className="w-5 h-5 text-primary-600" />
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-gray-900 mb-1">Social Features</h3>
                                        <p className="text-gray-600">
                                            Experience sharing, commenting, and community interactions
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Simple Steps */}
            <section className="section-padding bg-gray-50">
                <div className="container-custom">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl font-bold text-gray-900 mb-4">
                            How It Works
                        </h2>
                        <p className="text-gray-600 max-w-2xl mx-auto">
                            Three simple steps to experience Swag Shots's full potential
                        </p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-8">
                        <div className="text-center">
                            <div className="w-12 h-12 bg-primary-600 text-white rounded-full flex items-center justify-center font-bold text-lg mx-auto mb-4">
                                1
                            </div>
                            <h3 className="font-semibold text-gray-900 mb-2">Launch Demo</h3>
                            <p className="text-gray-600">
                                Click the demo button to start your interactive experience
                            </p>
                        </div>

                        <div className="text-center">
                            <div className="w-12 h-12 bg-primary-600 text-white rounded-full flex items-center justify-center font-bold text-lg mx-auto mb-4">
                                2
                            </div>
                            <h3 className="font-semibold text-gray-900 mb-2">Explore Features</h3>
                            <p className="text-gray-600">
                                Try video creation, filters, and social features hands-on
                            </p>
                        </div>

                        <div className="text-center">
                            <div className="w-12 h-12 bg-primary-600 text-white rounded-full flex items-center justify-center font-bold text-lg mx-auto mb-4">
                                3
                            </div>
                            <h3 className="font-semibold text-gray-900 mb-2">Download App</h3>
                            <p className="text-gray-600">
                                Ready to create? Download the full Swag Shots experience
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Call to Action */}
            <section className="section-padding">
                <div className="container-custom text-center">
                    <h2 className="text-3xl font-bold text-gray-900 mb-4">
                        Ready to Get Started?
                    </h2>
                    <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
                        Download Swag Shots on Android now and start creating amazing content. iOS is coming soon.
                    </p>

                    <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
                        <Button size="lg" className="inline-flex items-center gap-2" onClick={showIosComingSoon}>
                            iOS Coming Soon
                            <ArrowRight className="w-4 h-4" />
                        </Button>
                        <Button
                            size="lg"
                            variant="outline"
                            onClick={() => window.open('https://play.google.com/store/apps/details?id=com.swagshots.app', '_blank')}
                        >
                            Download for Android
                        </Button>
                    </div>

                    <Link
                        to="/"
                        className="text-primary-600 hover:text-primary-700 font-medium transition-colors"
                    >
                        ← Back to Home
                    </Link>
                </div>
            </section>
        </Layout>
    );
}
